import React, {
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useRef,
  type JSX,
} from 'react';
// eslint-disable-next-line no-restricted-imports
import {useInViewEffect} from '@aboutyou/ui-components';

type RenderPropChildren = (props: {onClickCapture: () => void}) => ReactElement;

export interface BaseTrackerProps {
  Comp?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  children?: ReactNode | RenderPropChildren;
  /**
   * @deprecated Use `track` instead and check the first argument for the type. `track` is save to use in combination with `triggerOnce`, `onImpressionTracked` might result in unwanted behavior and tracks more often.
   *
   * @memberof BaseTrackerProps
   */
  onImpressionTracked?: () => void;
  /**
   * @deprecated Use `track` instead and check the first argument for the type.
   *
   * @memberof BaseTrackerProps
   */
  onClickTracked?: () => void;
  /** A value that uniquely identifies the item to be tracked, e.g. a product or story ID */
  trackingKey?: string | number;
  threshold?: number;
  triggerOnce?: boolean;
  track: (type: 'impression' | 'click') => void;
  className?: string;
  id?: string;
  disabled?: boolean;
  disableClickTracking?: boolean;
}

export const BaseTracker: FC<BaseTrackerProps> = ({
  Comp = 'div',
  children,
  track,
  onClickTracked,
  onImpressionTracked,
  trackingKey,
  threshold,
  triggerOnce,
  disabled,
  disableClickTracking,
  ...rest
}) => {
  // Using a ref that is always up to date allows us to never re-generate the `trackClick` and `trackImpression` callbacks, but still have the most recent function call. This is important to respect `triggerOnce` even though the track function would update.
  const trackRef = useRef(track);
  trackRef.current = track;

  const trackClick = useCallback(() => {
    if (disableClickTracking) {
      return;
    }
    if (trackRef.current !== undefined) {
      trackRef.current('click');
    }
    onClickTracked?.();
  }, [trackRef, onClickTracked, disableClickTracking]);

  const trackImpression = useCallback(
    (inView: boolean) => {
      if (!inView || disabled) {
        return;
      }

      onImpressionTracked?.();
      trackRef.current('impression');
    },
    // tracking key is passed so that the inViewEffect triggers again if the key changes
    [trackRef, onImpressionTracked, trackingKey, disabled],
  );

  const {ref} = useInViewEffect<HTMLDivElement>(trackImpression, {
    threshold,
    triggerOnce,
  });

  return (
    <Comp {...rest} ref={ref} onClick={trackClick}>
      {children}
    </Comp>
  );
};
